import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from './ui/Button'

interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

const COOKIE_CONSENT_KEY = 'silux_cookie_consent'

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!consent) {
      setShowBanner(true)
    } else {
      try {
        const saved = JSON.parse(consent)
        setPreferences(saved)
      } catch {
        setShowBanner(true)
      }
    }
  }, [])

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs))
    setShowBanner(false)
    setShowPreferences(false)
    // Here you would typically initialize analytics/marketing scripts based on consent
    if (prefs.analytics) {
      // Initialize analytics (e.g., Google Analytics)
      console.log('Analytics enabled')
    }
    if (prefs.marketing) {
      // Initialize marketing cookies
      console.log('Marketing enabled')
    }
  }

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    }
    setPreferences(allAccepted)
    savePreferences(allAccepted)
  }

  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    }
    setPreferences(onlyNecessary)
    savePreferences(onlyNecessary)
  }

  const saveCustomPreferences = () => {
    savePreferences(preferences)
  }

  if (!showBanner) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-lg border-t">
      {showPreferences ? (
        <div className="max-w-4xl mx-auto p-6">
          <h3 className="text-lg font-semibold mb-4">Cookie Preferences</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <input
                type="checkbox"
                checked={preferences.necessary}
                disabled
                className="mt-1"
              />
              <div>
                <p className="font-medium">Necessary Cookies</p>
                <p className="text-sm text-gray-600">
                  Required for the website to function. Cannot be disabled.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <input
                type="checkbox"
                checked={preferences.analytics}
                onChange={(e) =>
                  setPreferences({ ...preferences, analytics: e.target.checked })
                }
                className="mt-1"
              />
              <div>
                <p className="font-medium">Analytics Cookies</p>
                <p className="text-sm text-gray-600">
                  Help us understand how visitors interact with our website to improve user
                  experience.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <input
                type="checkbox"
                checked={preferences.marketing}
                onChange={(e) =>
                  setPreferences({ ...preferences, marketing: e.target.checked })
                }
                className="mt-1"
              />
              <div>
                <p className="font-medium">Marketing Cookies</p>
                <p className="text-sm text-gray-600">
                  Used to deliver personalized advertisements and track ad campaign performance.
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button variant="primary" onClick={saveCustomPreferences}>
              Save Preferences
            </Button>
            <Button variant="outline" onClick={() => setShowPreferences(false)}>
              Back
            </Button>
          </div>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto p-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex-1">
              <p className="text-gray-700">
                We use cookies to enhance your browsing experience, serve personalized ads or
                content, and analyze our traffic. By clicking "Accept All", you consent to our use
                of cookies. Read our{' '}
                <Link to="/privacy" className="text-silux-primary hover:underline">
                  Privacy Policy
                </Link>{' '}
                for more information.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowPreferences(true)}>
                Preferences
              </Button>
              <Button variant="outline" size="sm" onClick={rejectAll}>
                Reject All
              </Button>
              <Button variant="primary" size="sm" onClick={acceptAll}>
                Accept All
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
